import React, { useState, useEffect } from 'react';
import { AppBar, Container, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

const NavContainer = styled(AppBar)`
  background: transparent;
  box-shadow: none;
  padding: 1rem 0;
  position: fixed;
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
  
  &.scrolled {
    background: rgba(0, 11, 24, 0.8);
    backdrop-filter: blur(10px);
  }
`;

const NavToolbar = styled(Toolbar)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;

  @media (max-width: 1200px) {
    padding: 0 1rem;
  }
`;

const NavLinks = styled(Box)`
  display: flex;
  gap: 2rem;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 2rem;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  position: relative;
  padding: 0.5rem 0;
  transition: color 0.3s ease;

  &:hover {
    color: #4facfe;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #4facfe, #00f2fe);
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const LogoContainer = styled(Box)`
  display: flex;
  align-items: center;
  min-width: 100px;
`;

const MenuContainer = styled(Box)`
  display: flex;
  align-items: center;
  min-width: 100px;
  justify-content: flex-end;
`;

const LogoText = styled(Typography)`
  font-family: 'Lobster', cursive;
  font-size: 2.5rem;
  cursor: pointer;
  margin-left: 0.5rem;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const NavButton = styled(Button)`
  color: white;
  margin-left: 1rem;
  text-transform: none;
  font-size: 1rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #4facfe, #00f2fe);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: #4facfe;
    background: transparent;
  }
  
  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const MobileMenu = styled(Menu)`
  .MuiPaper-root {
    background: #000B18;
    backdrop-filter: blur(10px);
    border-radius: 16px;
    margin-top: 1rem;
    border: 1px solid rgba(0, 229, 255, 0.1);
    min-width: 200px;
  }

  .MuiMenuItem-root {
    color: #f5f5f5;
    font-weight: 500;
    padding: 1rem 2rem;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(0, 229, 255, 0.1);
    }
  }
`;

const TechnologyMenu = styled(Menu)`
  .MuiPaper-root {
    background: #000B18;
    backdrop-filter: blur(10px);
    border-radius: 16px;
    margin-top: 1rem;
    border: 1px solid rgba(0, 229, 255, 0.1);
    min-width: 200px;
  }

  .MuiMenuItem-root {
    color: #f5f5f5;
    font-weight: 500;
    padding: 1rem 2rem;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(0, 229, 255, 0.1);
      color: #4facfe;
    }
  }
`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navItems = ['Technology', 'Industry', 'Products', 'Approach', 'Contact', 'Careers'];
  
  const handleNavigation = (item: string) => {
    const href = getHref(item);
    
    // Check if it's an external link (starts with '/')
    if (href.startsWith('/')) {
      navigate(href);
      // Reset scroll position after navigation
      window.scrollTo(0, 0);
      return;
    }
    
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: href } });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const getHref = (item: string) => {
    switch (item) {
      case 'Technology':
        return '#technology';
      case 'Industry':
        return '#industries';
      case 'Careers':
        return '/career';
      default:
        return `#${item.toLowerCase()}`;
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <NavContainer position="fixed" className={scrolled ? 'scrolled' : ''}>
      <Container maxWidth="lg">
        <NavToolbar disableGutters>
          <Box sx={{ 
            width: '200px',
            '@media (max-width: 768px)': {
              width: 'auto'
            }
          }}>
            <Box 
              component="a" 
              href="/"
              onClick={handleLogoClick}
              sx={{ textDecoration: 'none' }}
            >
              <LogoText variant="h1">
                Yurix
              </LogoText>
            </Box>
          </Box>

          <Box sx={{ 
            flex: 1,
            display: { xs: 'none', md: 'flex' }, 
            justifyContent: 'center',
            gap: '2rem',
            '@media (max-width: 768px)': {
              display: 'none'
            }
          }}>
            <AnimatePresence>
              {navItems.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <NavButton
                    onClick={() => handleNavigation(item)}
                  >
                    {item}
                  </NavButton>
                </motion.div>
              ))}
            </AnimatePresence>
          </Box>

          <Box sx={{ 
            width: '200px',
            display: 'flex',
            justifyContent: 'flex-end',
            '@media (max-width: 768px)': {
              width: 'auto'
            }
          }}>
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMenu}
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                color: '#f5f5f5',
                '&:hover': {
                  background: 'rgba(79, 172, 254, 0.1)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <MobileMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {navItems.map((item) => (
                <MenuItem 
                  key={item} 
                  onClick={() => {
                    handleClose();
                    handleNavigation(item);
                  }}
                >
                  {item}
                </MenuItem>
              ))}
            </MobileMenu>
          </Box>
        </NavToolbar>
      </Container>
    </NavContainer>
  );
};

export default Navbar; 